// ShellSurfaceContext — orchestration for the shell-level surfaces (Epic 4).
//
// The inbox drawer, the running-timer panel and global search are overlays that
// open FROM the shell (the top bar) and from anywhere a page calls in (a mention
// opens the inbox at a thread; a work card starts the timer). One provider holds
// which surface is open + the inbox's active thread, so a mention click can open
// the inbox drawer already focused on a thread WITHOUT a route change.
//
// Only one overlay is open at a time — opening a second closes the first. None of
// this is a route; the URL never changes (00_CONTRACT.md §12: "never a navigation
// away from the page — someone who set a filter must never lose it to see a
// detail").

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const ShellSurfaceContext = createContext(null);

export function ShellSurfaceProvider({ children }) {
  // `inbox.threadId` opens the inbox already focused on a thread (a mention, or a
  // deep link). null = the inbox list.
  const [inbox, setInbox] = useState({ open: false, threadId: null });
  const [search, setSearch] = useState(false);
  const [timer, setTimer] = useState(false);

  const openInbox = useCallback((threadId = null) => {
    setInbox({ open: true, threadId });
    setSearch(false);
    setTimer(false);
  }, []);
  const closeInbox = useCallback(
    () => setInbox((s) => ({ ...s, open: false, threadId: null })),
    [],
  );

  const openSearch = useCallback(() => {
    setSearch(true);
    setInbox({ open: false, threadId: null });
    setTimer(false);
  }, []);
  const closeSearch = useCallback(() => setSearch(false), []);

  const openTimer = useCallback(() => {
    setTimer(true);
    setInbox({ open: false, threadId: null });
    setSearch(false);
  }, []);
  const closeTimer = useCallback(() => setTimer(false), []);

  const closeAll = useCallback(() => {
    setInbox({ open: false, threadId: null });
    setSearch(false);
    setTimer(false);
  }, []);

  const value = useMemo(
    () => ({
      inbox,
      openInbox,
      closeInbox,
      search,
      openSearch,
      closeSearch,
      timer,
      openTimer,
      closeTimer,
      closeAll,
    }),
    [
      inbox,
      openInbox,
      closeInbox,
      search,
      openSearch,
      closeSearch,
      timer,
      openTimer,
      closeTimer,
      closeAll,
    ],
  );

  return (
    <ShellSurfaceContext.Provider value={value}>
      {children}
    </ShellSurfaceContext.Provider>
  );
}

export function useShellSurface() {
  const ctx = useContext(ShellSurfaceContext);
  if (!ctx) {
    throw new Error('useShellSurface must be used within a ShellSurfaceProvider');
  }
  return ctx;
}
